import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FocusMarketingSkeleton from './FocusMarketingSkeleton';
import ErrorState from '../ErrorState/ErrorState';
import { useCachedFetch } from './../../hooks/useCachedFetch';
import { AnimatedOnScroll } from '../AnimatedOnScroll';
import { IMAGES_BASE_URL } from '../../config/constants';
import './FocusMarketing.scss';

const FocusMarketing = ({ page }) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  const { data: focusData, loading: dataLoading, error: dataError, refetch } = useCachedFetch('/data/locales/focus.json');

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (dataLoading) return;

    if (dataError) {
      setHasError(true);
      setIsLoading(false);
      return;
    }

    const arrowImage = focusData?.focusData?.arrowImage;
    const data = focusData?.focusData?.[page];

    if (data && data.quoteTitle && data.quoteText && arrowImage) {
      setHasError(false);
    } else {
      setHasError(true);
    }

    setIsLoading(false);
  }, [dataLoading, dataError, focusData, page]);

  const focusMarketingData = useMemo(() => {
    if (!focusData) return null;

    const arrowImage = focusData.focusData?.arrowImage;
    const data = focusData.focusData?.[page];

    if (data && data.quoteTitle && data.quoteText && arrowImage) {
      return { data, arrow: arrowImage };
    }

    return null;
  }, [focusData, page]);

  const getTranslatedText = useCallback((textData) => {
    if (typeof textData === 'object') {
      if (textData[currentLang] !== undefined) return textData[currentLang];
      if (textData.en !== undefined) return textData.en;
      const firstLang = Object.keys(textData)[0];
      return textData[firstLang];
    }
    return textData;
  }, [currentLang]);

  const translatedQuoteTitle = useMemo(() =>
    focusMarketingData ? getTranslatedText(focusMarketingData.data.quoteTitle) : '',
    [focusMarketingData, getTranslatedText]
  );

  const translatedQuoteText = useMemo(() =>
    focusMarketingData ? getTranslatedText(focusMarketingData.data.quoteText) : '',
    [focusMarketingData, getTranslatedText]
  );

  if (isLoading) {
    return <FocusMarketingSkeleton />;
  }

  if (hasError || !focusMarketingData) {
    return <ErrorState onRetry={handleRetry} />;
  }

  const { arrow: currentArrow } = focusMarketingData;

  return (
    <div className="focus__marketing__section">
      <div className="design__arrow">
        <div className="arrow__wrapper"></div>
        <img src={`${IMAGES_BASE_URL}${currentArrow}`} alt="arrow" className="arrow__down" loading="lazy" />
      </div>
      <div className="focus__marketing__section__wrapper">
        <div className="focus__marketing__section__item__desc">
          <div className="focus__marketing__section__item__desc__quote">
            {Array.isArray(translatedQuoteTitle) ? (
              translatedQuoteTitle.map((line, index) => (
                <AnimatedOnScroll animation="fade-up" delay={0} key={index}>
                  <h3 key={index} className="focus__marketing__section__item__desc__quote__title">
                    {line}
                  </h3>
                </AnimatedOnScroll>
              ))
            ) : (
              <h3 className="focus__marketing__section__item__desc__quote__title">
                {translatedQuoteTitle}
              </h3>
            )}
            <span className="focus__marketing__section__item__desc__quote__line" />
          </div>
          <div className="focus__marketing__section__item__desc__article">
            <AnimatedOnScroll animation="fade-up" delay={0}>
              <p className="focus__marketing__section__item__desc__article__text">
                {translatedQuoteText}
              </p>
            </AnimatedOnScroll>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(FocusMarketing);